import React from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Terminal,
  PlayArrow,
  Close,
  OpenInFull,
  CloseFullscreen,
  Info,
  CheckCircle,
  Error,
  Schedule,
  Memory,
  Stars,
  EmojiEvents,
} from "@mui/icons-material";
import CelebrationAnimation from "../../animations/CelebrationAnimation";

/**
 * Output Panel Component - Displays test cases, custom input, and results
 */
const OutputPanel = ({
  showOutput,
  setShowOutput,
  activeTab,
  setActiveTab,
  testCases,
  customInput,
  setCustomInput,
  customResult,
  customLoading,
  onRunCustomInput,
  results,
  summary,
  showCelebration,
  setShowCelebration,
  isOutputFloating,
  onFloatingToggle,
  vtLoading,
  outputHeight = 200,
  isEditorFullscreen = false,
  isMobile = false,
}) => {
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // When in floating mode, always show content (FloatingOutputWindow handles the container)
  // When not in floating mode, only show if showOutput is true
  const shouldShow = isOutputFloating || showOutput;

  return (
    <Box
      sx={{
        height: isOutputFloating ? "100%" : showOutput ? outputHeight : 0,
        border: isOutputFloating
          ? "none"
          : showOutput
            ? "1px solid #e0e0e0"
            : "1px solid transparent",
        borderRadius: isOutputFloating ? 0 : "8px",
        bgcolor: "#f8f9fa",
        display: shouldShow ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: isOutputFloating ? 1 : 1000,
        transition: isOutputFloating ? "none" : "all 0.3s ease-in-out",
        opacity: shouldShow ? 1 : 0,
        transform: isOutputFloating
          ? "none"
          : showOutput
            ? "translateY(0)"
            : "translateY(10px)",
        flexShrink: 0,
        width: "100%",
      }}
    >
      {/* Output Header with Tabs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 0.5,
          borderBottom: "1px solid #e0e0e0",
          bgcolor: "#fff",
        }}
      >
        <Terminal
          sx={{
            fontSize: "18px",
            color: "#00C49A",
            display: { base: "none", xs: "block" },
          }}
        />
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#333",
            mr: { base: 0, sm: 2 },
            display: { base: "none", sm: "block" },
          }}
        >
          Output
        </Typography>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            flex: 1,
            minHeight: "auto",
            "& .MuiTabs-indicator": {
              backgroundColor: "#00C49A",
            },
            "& .MuiTabs-scrollButtons": {
              width: "auto",
              "&.Mui-disabled": {
                opacity: 0.3,
              },
            },
          }}
        >
          <Tab
            label="Test Cases"
            iconPosition="start"
            sx={{
              minHeight: "auto",
              py: 1,
              px: { base: 2, md: 3 },
              fontSize: { base: "13px", md: "12px" },
              fontWeight: 500,
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              "&.Mui-selected": {
                color: "#00C49A",
              },
            }}
          />
          <Tab
            label="Custom Input"
            iconPosition="start"
            sx={{
              minHeight: "auto",
              py: 1,
              px: { base: 2, md: 3 },
              fontSize: { base: "13px", md: "12px" },
              fontWeight: 500,
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              "&.Mui-selected": {
                color: "#00C49A",
              },
            }}
          />
          <Tab
            label="Results"
            iconPosition="start"
            sx={{
              minHeight: "auto",
              py: 1,
              px: { base: 2, md: 3 },
              fontSize: { base: "13px", md: "12px" },
              fontWeight: 500,
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              "&.Mui-selected": {
                color: "#00C49A",
              },
            }}
          />
        </Tabs>

        {/* Floating Toggle Button - Hidden in fullscreen mode and mobile */}
        {!isOutputFloating && !isEditorFullscreen && !isMobile && (
          <IconButton
            onClick={onFloatingToggle}
            size="small"
            sx={{
              mr: 1,
              backgroundColor: "#00C49A",
              color: "white",
              minWidth: "44px",
              minHeight: "44px",
              "&:hover": {
                backgroundColor: "#00a085",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease",
            }}
            title={"Enter Floating Mode"}
          >
            <OpenInFull sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        {!isOutputFloating && (
          <IconButton
            onClick={() => setShowOutput(false)}
            size="small"
            sx={{
              ml: "auto",
              minWidth: "44px",
              minHeight: "44px",
            }}
          >
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Tab Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          bgcolor: "#fff",
        }}
      >
        {/* Test Cases Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 1 }}>
            {vtLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={24} sx={{ color: "#00C49A" }} />
              </Box>
            ) : (
              <Stack spacing={2}>
                {testCases
                  .filter((testCase) => testCase.visible !== false) // Show if visible is true or undefined/null (backward compatibility)
                  .map((testCase, index) => (
                    <Box
                      key={index}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        p: 2,
                        bgcolor: "#f8f9fa",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Test Case {index + 1}
                      </Typography>
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, width: "100%" }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              fontSize: { base: "13px", md: "12px" },
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Input
                          </Typography>
                          <Box
                            sx={{
                              p: { base: 1.5, md: 2 },
                              bgcolor: "#f8f9fa",
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              fontFamily: "monospace",
                              fontSize: { base: "12px", md: "11px" },
                              whiteSpace: "pre-wrap",
                              minHeight: { base: 50, md: 60 },
                            }}
                          >
                            {testCase.input}
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, width: "100%" }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              fontSize: { base: "13px", md: "12px" },
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Expected Output
                          </Typography>
                          <Box
                            sx={{
                              p: { base: 1.5, md: 2 },
                              bgcolor: "#f8f9fa",
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              fontFamily: "monospace",
                              fontSize: { base: "12px", md: "11px" },
                              whiteSpace: "pre-wrap",
                              minHeight: { base: 50, md: 60 },
                            }}
                          >
                            {testCase.output}
                          </Box>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            )}
          </Box>
        )}

        {/* Custom Input Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 1 }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontSize: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Custom Input
                </Typography>
                <TextField
                  multiline
                  rows={isMobile ? 6 : 4} // Conditional prop usage is acceptable for rows if not responsive prop
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  placeholder="Enter custom input here..."
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "monospace",
                      fontSize: { base: "14px", md: "12px" },
                    },
                    "& .MuiInputBase-input": {
                      minHeight: { base: "44px", md: "auto" },
                    },
                  }}
                />
              </Box>
              <Box>
                <Button
                  variant="contained"
                  onClick={() => onRunCustomInput(customInput)}
                  disabled={customLoading || !customInput.trim()}
                  startIcon={
                    customLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PlayArrow />
                    )
                  }
                  sx={{
                    bgcolor:
                      customLoading || !customInput.trim()
                        ? "#E0E0E0"
                        : "#00C49A",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: { base: "14px", md: "14px" },
                    borderRadius: "8px",
                    height: { base: "44px", md: "36px" },
                    width: { base: "100%", sm: "auto" },
                    minWidth: "fit-content",
                    px: 3,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor:
                        customLoading || !customInput.trim()
                          ? "#E0E0E0"
                          : "#00A085",
                    },
                    "&:disabled": {
                      bgcolor: "#E0E0E0",
                      color: "#9E9E9E",
                    },
                  }}
                >
                  {customLoading ? "Running..." : "Run"}
                </Button>
              </Box>
              {customResult && (
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Result
                  </Typography>
                  <Stack direction={{ base: "column", md: "row" }} spacing={2}>
                    <Box sx={{ flex: 1, width: "100%" }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: { base: "13px", md: "12px" },
                        }}
                      >
                        Stdout
                      </Typography>
                      <Box
                        sx={{
                          p: { base: 1.5, md: 2 },
                          bgcolor: "#f8f9fa",
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          fontFamily: "monospace",
                          fontSize: { base: "12px", md: "11px" },
                          whiteSpace: "pre-wrap",
                          minHeight: { base: 50, md: 60 },
                        }}
                      >
                        {customResult.result?.stdout ||
                          customResult.results?.[0]?.actual ||
                          "—"}
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, width: "100%" }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: { base: "13px", md: "12px" },
                        }}
                      >
                        Stderr
                      </Typography>
                      <Box
                        sx={{
                          p: { base: 1.5, md: 2 },
                          bgcolor: "#f8f9fa",
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          fontFamily: "monospace",
                          fontSize: { base: "12px", md: "11px" },
                          whiteSpace: "pre-wrap",
                          minHeight: { base: 50, md: 60 },
                          color: "#d32f2f",
                        }}
                      >
                        {customResult.result?.stderr ||
                          (customResult.results?.[0]?.status ===
                            "Compilation Error" ||
                          customResult.results?.[0]?.status === "Runtime Error"
                            ? customResult.results?.[0]?.actual
                            : "") ||
                          "—"}
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {/* Results Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 1 }}>
            {/* Celebration Animation */}
            {showCelebration && (
              <CelebrationAnimation
                isVisible={showCelebration}
                onComplete={() => setShowCelebration(false)}
              />
            )}

            {summary && (
              <Box
                sx={{
                  p: { base: 1.5, xs: 2 },
                  mb: 2.5,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  background: summary.message?.includes("Error")
                    ? "linear-gradient(135deg, #FFF5F5, #FFE3E3)"
                    : summary.message?.includes("Passed") ||
                        summary.message?.includes("Accepted") ||
                        summary.message?.includes("100/")
                      ? "linear-gradient(135deg, #F0FAF8, #D1F2EB)"
                      : "linear-gradient(135deg, #FFF9F2, #FFF0E0)",
                  border: `1px solid ${
                    summary.message?.includes("Error")
                      ? "#FFAFB0"
                      : summary.message?.includes("Passed") ||
                          summary.message?.includes("Accepted") ||
                          summary.message?.includes("100/")
                        ? "#A3E4D7"
                        : "#FFD5A3"
                  }`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    bgcolor: summary.message?.includes("Error")
                      ? "#f44336"
                      : summary.message?.includes("Passed") ||
                          summary.message?.includes("Accepted") ||
                          summary.message?.includes("100/")
                        ? "#00C49A"
                        : "#ff9800",
                    color: "#fff",
                  }}
                >
                  {summary.message?.includes("Error") ? (
                    <Error sx={{ fontSize: 22 }} />
                  ) : summary.message?.includes("Passed") ||
                    summary.message?.includes("Accepted") ||
                    summary.message?.includes("100/") ? (
                    <EmojiEvents sx={{ fontSize: 22 }} />
                  ) : (
                    <Info sx={{ fontSize: 22 }} />
                  )}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: "#1a1a2e",
                      fontSize: { base: "14px", xs: "15px" },
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    {summary.message?.includes("/")
                      ? "Final Score"
                      : "Assessment Result"}
                  </Typography>
                  <Typography
                    sx={{
                      color: summary.message?.includes("Error")
                        ? "#d32f2f"
                        : summary.message?.includes("Passed") ||
                            summary.message?.includes("Accepted")
                          ? "#00876c"
                          : "#e65100",
                      fontSize: { base: "12px", xs: "13px" },
                      fontWeight: 600,
                      mt: 0.3,
                    }}
                  >
                    {summary.message}
                  </Typography>
                </Box>
              </Box>
            )}

            {results?.length > 0 && (
              <Box>
                {/* Desktop Table View (sm and up) */}
                <TableContainer
                  component={Paper}
                  sx={{
                    display: { base: "none", sm: "block" },
                    border: "1px solid #eee",
                    borderRadius: "8px",
                    boxShadow: "none",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#fafafa" }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          #
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Time
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Memory
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Score
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.map((result, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontSize: "12px" }}>
                            {index + 1}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "12px",
                              color:
                                result.status === "Passed" ||
                                result.status === "Accepted"
                                  ? "#2e7d32"
                                  : "#d32f2f",
                              fontWeight: 600,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              {result.status}
                              {(result.status === "Runtime Error" ||
                                result.status === "Compilation Error") && (
                                <Tooltip
                                  title={result.actual || "Error Detail"}
                                  arrow
                                >
                                  <Info
                                    sx={{ fontSize: 16, color: "#f44336" }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px" }}>
                            {result.time || "—"}s
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px" }}>
                            {result.memory || "—"} KB
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px" }}>
                            {result.score}/{result.maxScore}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Mobile Card View (base to sm) */}
                <Stack
                  spacing={1.5}
                  sx={{ display: { base: "flex", sm: "none" } }}
                >
                  {results.map((result, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        border: "1px solid #eee",
                        bgcolor: "#fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.5}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#888",
                          }}
                        >
                          TEST CASE #{index + 1}
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.3,
                            borderRadius: "4px",
                            bgcolor:
                              result.status === "Passed" ||
                              result.status === "Accepted"
                                ? "#e8f5e9"
                                : "#ffebee",
                            color:
                              result.status === "Passed" ||
                              result.status === "Accepted"
                                ? "#2e7d32"
                                : "#d32f2f",
                            fontSize: "11px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          {result.status}
                        </Box>
                      </Stack>

                      <Stack
                        direction={{ base: "column", xs: "row" }}
                        spacing={1.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Box sx={{ minWidth: { base: "100%", xs: "45%" } }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Schedule sx={{ fontSize: 14, color: "#999" }} />
                            <Typography
                              sx={{ fontSize: "11px", color: "#666" }}
                            >
                              Time:
                            </Typography>
                            <Typography
                              sx={{ fontSize: "11px", fontWeight: 600 }}
                            >
                              {result.time || "—"}s
                            </Typography>
                          </Stack>
                        </Box>
                        <Box sx={{ minWidth: { base: "100%", xs: "45%" } }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Memory sx={{ fontSize: 14, color: "#999" }} />
                            <Typography
                              sx={{ fontSize: "11px", color: "#666" }}
                            >
                              Memory:
                            </Typography>
                            <Typography
                              sx={{ fontSize: "11px", fontWeight: 600 }}
                            >
                              {result.memory || "—"} KB
                            </Typography>
                          </Stack>
                        </Box>
                        <Box
                          sx={{
                            minWidth: { base: "100%", xs: "45%" },
                            mt: { base: 0, xs: 0.5 },
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Stars sx={{ fontSize: 14, color: "#999" }} />
                            <Typography
                              sx={{ fontSize: "11px", color: "#666" }}
                            >
                              Score:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#00C49A",
                              }}
                            >
                              {result.score}/{result.maxScore}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            {!results?.length && !summary && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#666",
                    fontSize: "14px",
                    mb: 1,
                  }}
                >
                  No results yet
                </Typography>
                <Typography
                  sx={{
                    color: "#999",
                    fontSize: "12px",
                  }}
                >
                  Run your code to see results.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OutputPanel;
